import Button, { type ButtonProps } from '@components/atoms/Button'

import type { ComponentDoc, PropDocs } from '../types'

const props: PropDocs<ButtonProps> = {
  rank: {
    type: "'primary' | 'secondary' | 'link'",
    default: "'primary'",
    description: 'Visual prominence. Combined with color to pick the palette.',
  },
  color: {
    type: "'primary' | 'danger' | 'success' | 'warning'",
    default: "'primary'",
    description: 'Colour family. Combined with rank to pick the palette.',
  },
  size: { type: "'sm' | 'md' | 'xl' | '2xl'", default: "'md'", description: 'Padding and type scale.' },
  leadingIcon: { type: 'IconTypes', description: 'Sprite glyph before the label, 18px.' },
  trailingIcon: { type: 'IconTypes', description: 'Sprite glyph after the label, 18px.' },
  children: { type: 'ReactNode', description: 'The label. Required unless ariaLabel is given.' },
  ariaLabel: {
    type: 'string',
    description: 'Accessible name. The type system requires one of children or ariaLabel.',
  },
  onClick: { type: '(event?: MouseEvent) => void', description: 'Click handler.' },
  type: { type: "'button' | 'submit' | 'reset'", default: "'button'", description: 'Native button type.' },
  disabled: { type: 'boolean', description: 'Sets both disabled and aria-disabled.' },
  linkBackground: {
    type: 'boolean',
    default: 'true',
    description: 'For rank="link": whether hover paints a tinted background.',
  },
  className: { type: 'string', description: 'Extra classes, merged with cn so conflicts resolve to yours.' },
  dataCompId: { type: 'string', description: 'Renders data-compid="<id>-button".' },
  role: { type: 'string', description: 'Passed through to the button element.' },
  ref: { type: 'Ref<HTMLButtonElement>', description: 'Forwarded to the button element.' },
}

const doc: ComponentDoc<ButtonProps> = {
  slug: 'button',
  name: 'Button',
  tier: 'atom',
  importStatement: "import { Button } from '@atoms'",
  sourcePath: 'app/frontend/components/atoms/Button.tsx',
  component: Button,
  props,
  examples: [
    {
      title: 'Ranks',
      variants: [
        { rank: 'primary', children: 'Primary' },
        { rank: 'secondary', children: 'Secondary' },
        { rank: 'link', children: 'Link' },
      ],
    },
    {
      title: 'Colours',
      variants: [
        { color: 'primary', children: 'Primary' },
        { color: 'danger', children: 'Danger' },
        { color: 'success', children: 'Success' },
        { color: 'warning', children: 'Warning' },
        { rank: 'secondary', color: 'danger', children: 'Secondary danger' },
      ],
    },
    {
      title: 'Sizes',
      variants: [
        { size: 'sm', children: 'Small' },
        { size: 'md', children: 'Medium' },
        { size: 'xl', children: 'Extra large' },
        { size: '2xl', children: '2XL' },
      ],
    },
    {
      title: 'With icons',
      note: 'An icon-only button must pass ariaLabel; the prop types enforce it.',
      variants: [
        { leadingIcon: 'plus', children: 'Leading icon' },
        { rank: 'secondary', trailingIcon: 'arrow-narrow-right', children: 'Trailing icon' },
        { rank: 'secondary', leadingIcon: 'trash-01', ariaLabel: 'Icon only' },
      ],
    },
    {
      title: 'Disabled',
      variants: [
        { disabled: true, children: 'Disabled' },
        { rank: 'secondary', disabled: true, children: 'Disabled secondary' },
      ],
    },
  ],
  a11y: [
    'Renders a native <button>; the prop types require children or ariaLabel.',
    'disabled sets aria-disabled, and aria-disabled:cursor-not-allowed in ButtonStyles.',
    'Visible focus ring: focus-visible:ring-4 in primary-300.',
  ],
}

export default doc
